import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, Building2, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Register() {
  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'yearly';
  const isDirect = searchParams.get('mode') === 'direct';

  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);

  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[%&?!@#$^*]).{6,}$/;

  const validatePassword = (pwd) => {
    if (!pwd) return 'Şifre gerekli';
    if (!PASSWORD_REGEX.test(pwd)) return 'Şifre kurallara uymuyor';
    return '';
  };

  // Real-time confirm password error: show when same length OR field was blurred
  const confirmError = (() => {
    const { password, confirmPassword } = form;
    if (!confirmPassword) return '';
    const lengthMatch = confirmPassword.length >= password.length;
    if ((confirmTouched || lengthMatch) && confirmPassword !== password) return 'Şifreler eşleşmiyor';
    return '';
  })();

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'İsim gerekli';
    if (!form.email.trim()) e.email = 'E-posta gerekli';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Geçerli bir e-posta girin';
    const pwdErr = validatePassword(form.password);
    if (pwdErr) e.password = pwdErr;
    if (!form.confirmPassword) e.confirmPassword = 'Şifre tekrarı gerekli';
    else if (form.password !== form.confirmPassword) e.confirmPassword = 'Şifreler eşleşmiyor';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        name: form.name,
        email: form.email,
        password: form.password,
        role: 'organizer',
      });
      localStorage.setItem('token', res.data.access_token);
      setUser(res.data.user);

      if (isDirect) {
        // Direct buy mode: go straight to Stripe checkout
        toast.success('Hesap oluşturuldu! Ödeme sayfasına yönlendiriliyorsunuz...');
        try {
          const checkoutRes = await axios.post(
            `${API}/billing/create-checkout-session`,
            { plan_id: planId, origin_url: window.location.origin },
            { headers: { Authorization: `Bearer ${res.data.access_token}` } }
          );
          window.location.href = checkoutRes.data.url;
        } catch {
          toast.error('Ödeme başlatılamadı, lütfen tekrar deneyin');
          navigate('/pricing');
        }
      } else {
        toast.success('Kayıt başarılı! 14 günlük denemeniz başladı.');
        navigate('/organizer');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Kayıt başarısız';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const planLabel = planId === 'yearly' ? 'Yıllık Plan - $375/yıl' : 'Aylık Plan - $39/ay';

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-emerald-50 to-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 group"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-lg group-hover:text-teal-700 transition-colors">
              Expogrid.io
            </span>
          </button>
          <button
            onClick={() => navigate('/login')}
            className="text-sm text-teal-600 hover:text-teal-800 font-medium"
          >
            Giriş Yap
          </button>
        </div>
      </header>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Plan badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              <CheckCircle className="w-3 h-3" />
              {isDirect ? `Satın Al: ${planLabel}` : `Ücretsiz Deneme: ${planLabel}`}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Hesap Oluşturun</h1>
            <p className="text-gray-500 text-sm">
              {isDirect
                ? 'Hesabınızı oluşturun ve hemen ödemeye geçin'
                : '14 gün boyunca ücretsiz tüm özellikleri deneyin'}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
              {/* Name */}
              <div className="space-y-1.5">
                <Label htmlFor="reg-name" className="text-sm font-medium text-gray-700">
                  Ad Soyad
                </Label>
                <Input
                  id="reg-name"
                  type="text"
                  placeholder="Adınız Soyadınız"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={errors.name ? 'border-red-400' : ''}
                  data-testid="register-name-input"
                />
                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="reg-email" className="text-sm font-medium text-gray-700">
                  E-posta Adresi
                </Label>
                <Input
                  id="reg-email"
                  type="email"
                  placeholder="ornek@sirket.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={errors.email ? 'border-red-400' : ''}
                  data-testid="register-email-input"
                />
                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <Label htmlFor="reg-password" className="text-sm font-medium text-gray-700">
                  Şifre
                </Label>
                <div className="relative">
                  <Input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Şifrenizi girin"
                    value={form.password}
                    onChange={(e) => {
                      setForm({ ...form, password: e.target.value });
                      if (passwordTouched) {
                        const err = validatePassword(e.target.value);
                        setErrors(prev => ({ ...prev, password: err }));
                      }
                    }}
                    onBlur={() => {
                      setPasswordTouched(true);
                      const err = validatePassword(form.password);
                      setErrors(prev => ({ ...prev, password: err }));
                    }}
                    className={`pr-10 ${errors.password ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500">{errors.password}</p>
                )}
                {/* Password rules hint */}
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  En az 6 karakter, 1 büyük harf, 1 küçük harf ve 1 özel karakter (%&?!@#) içermelidir.
                </p>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <Label htmlFor="reg-confirm" className="text-sm font-medium text-gray-700">
                  Şifre Tekrar
                </Label>
                <div className="relative">
                  <Input
                    id="reg-confirm"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Şifrenizi tekrar girin"
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    onBlur={() => setConfirmTouched(true)}
                    className={`pr-10 ${(confirmError || errors.confirmPassword) ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
                    data-testid="register-confirm-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-confirm-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {(confirmError || errors.confirmPassword) && (
                  <p className="text-xs text-red-500">{confirmError || errors.confirmPassword}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white font-bold rounded-xl text-sm transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                data-testid="register-submit-btn"
              >
                {loading
                  ? (isDirect ? 'Hesap oluşturuluyor...' : 'Hesap oluşturuluyor...')
                  : (isDirect ? 'Hesap Oluştur ve Ödemeye Geç' : 'Hesap Oluştur ve Başla')}
              </button>
            </form>

            <div className="mt-5 text-center">
              <span className="text-sm text-gray-500">Zaten hesabınız var mı? </span>
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-teal-600 hover:text-teal-800 font-semibold"
                data-testid="goto-login-btn"
              >
                Giriş Yapın
              </button>
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-6 mt-6 text-xs text-gray-400">
            {isDirect ? (
              <>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-teal-500" />Güvenli ödeme</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-teal-500" />Anlık aktivasyon</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-teal-500" />İstediğiniz zaman iptal</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-teal-500" />Kredi kartı gerekmez</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-teal-500" />14 gün ücretsiz</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-teal-500" />İstediğiniz zaman iptal</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
